/**
 * Live miniature of a dashboard for library cards — the real renderer, scaled
 * down and clipped to a portrait frame, so people recognise a dashboard the way
 * they recognise a deck by its cover. Shares the studio's query cache.
 */
import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { BarChart3, Loader2 } from 'lucide-react';
import { coworkApi } from '@/cowork/omniad/api/coworkApi';
import { deriveTheme } from '@/cowork/omniad/components/slides/SlideRenderer';
import { DashboardRenderer } from './DashboardRenderer';
import type { DashboardSpec, Dataset } from './spec';

/** Width the dashboard is laid out at before scaling — a comfortable desktop width. */
const LAYOUT_WIDTH = 1180;

export function DashboardPreview({ dashboardId, hasSpec, accent }: { dashboardId: string; hasSpec: boolean; accent?: string }) {
  const frame = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0.22);
  useLayoutEffect(() => {
    const el = frame.current;
    if (!el) return;
    const measure = () => setScale(Math.max(0.05, el.clientWidth / LAYOUT_WIDTH));
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const q = useQuery({ queryKey: ['cowork', 'dashboard', dashboardId], queryFn: () => coworkApi.dashboards.get(dashboardId), staleTime: 5 * 60_000, enabled: hasSpec });
  const d = q.data as (typeof q.data & { spec?: DashboardSpec | null; data?: Record<string, Dataset> | null; brand?: Record<string, unknown> | null }) | undefined;
  const theme = useMemo(() => deriveTheme(d?.brand ?? {}), [d?.brand]);
  const brand = d?.brand as any;
  const logoUrl = brand?.brand?.logoUrl || brand?.logoUrl || theme.logoUrl || null;
  const brandName = brand?.brand?.name || brand?.name || theme.brandName;
  return (
    <div ref={frame} style={{ position: 'absolute', inset: 0, overflow: 'hidden', background: '#f8f9fc' }} aria-hidden="true">
      {hasSpec && d?.spec ? (
        <div style={{ width: LAYOUT_WIDTH, transform: `scale(${scale})`, transformOrigin: 'top left', pointerEvents: 'none' }}>
          <DashboardRenderer spec={d.spec} data={d.data || null} theme={theme} brandName={brandName} logoUrl={logoUrl} refreshedAt={d.lastRefreshedAt || null} />
        </div>
      ) : (
        <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', color: '#d0d5dd' }}>
          {hasSpec && q.isPending ? <Loader2 className="h-5 w-5 animate-spin" /> : <BarChart3 className="h-8 w-8" style={{ color: accent || '#d0d5dd', opacity: 0.6 }} />}
        </div>
      )}
      {/* Soft fade at the bottom so the clipped edge reads as "continues below". */}
      <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 36, background: 'linear-gradient(180deg, rgba(248,249,252,0) 0%, rgba(248,249,252,0.9) 100%)' }} />
    </div>
  );
}
